import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { useContentStore } from "@/stores/contentStore";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface NewArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  prefillKeyword?: string;
  prefillDate?: string;
}

const CONTENT_TYPES = [
  { id: "blog_post", label: "Blog Post" },
  { id: "glossary", label: "Glossary" },
  { id: "comparison", label: "Comparison" },
] as const;

export function NewArticleModal({
  isOpen,
  onClose,
  projectId,
  prefillKeyword,
  prefillDate,
}: NewArticleModalProps) {
  const createItem = useContentStore((s) => s.createItem);
  const { toast } = useToast();

  const [contentType, setContentType] = useState("blog_post");
  const [keyword, setKeyword] = useState("");
  const [blogIdea, setBlogIdea] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [targetWordCount, setTargetWordCount] = useState<number | "">("");
  const [intent, setIntent] = useState("");
  const [publishDate, setPublishDate] = useState<Date | undefined>();
  const [autoSocial, setAutoSocial] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Prefill on open
  useEffect(() => {
    if (isOpen) {
      setKeyword(prefillKeyword || "");
      setPublishDate(prefillDate ? new Date(prefillDate) : undefined);
      setContentType("blog_post");
      setBlogIdea("");
      setShowMore(!!prefillDate);
      setTargetWordCount("");
      setIntent("");
      setAutoSocial(true);
    }
  }, [isOpen, prefillKeyword, prefillDate]);

  const handleSubmit = async (startPipeline?: boolean) => {
    if (!keyword.trim()) return;

    setSubmitting(true);
    try {
      const stage = publishDate ? "scheduled" : "queued";
      const title = blogIdea?.trim() || keyword.trim();

      await createItem(projectId, {
        title,
        content_type: contentType,
        stage,
        primary_keyword: keyword.trim(),
        blog_idea: blogIdea?.trim() || undefined,
        target_word_count: targetWordCount || undefined,
        scheduled_publish_date: publishDate ? format(publishDate, "yyyy-MM-dd") : undefined,
        auto_social: autoSocial,
        metadata: {
          ...(intent ? { search_intent: intent } : {}),
          ...(startPipeline ? { auto_start: true } : {}),
        },
      });

      toast({ title: publishDate ? "Article scheduled" : "Article queued" });
      onClose();
    } catch {
      toast({ title: "Failed to create article", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>New Article</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Content type tabs */}
          <div className="flex gap-1 p-1 bg-muted/30 rounded-md" role="radiogroup" aria-label="Content type">
            {CONTENT_TYPES.map((t) => (
              <button
                key={t.id}
                role="radio"
                aria-checked={contentType === t.id}
                onClick={() => setContentType(t.id)}
                className={cn(
                  "flex-1 px-3 py-1.5 text-sm rounded-md transition-colors",
                  contentType === t.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Blog Idea */}
          <div className="space-y-2">
            <Label htmlFor="article-blog-idea" className="text-xs">Blog Idea / Angle (optional)</Label>
            <Textarea
              id="article-blog-idea"
              placeholder="e.g. Why X is better than Y for beginners..."
              value={blogIdea}
              onChange={(e) => setBlogIdea(e.target.value)}
              rows={2}
            />
          </div>

          {/* Primary keyword */}
          <div className="space-y-2">
            <Label htmlFor="article-keyword" className="text-xs">Primary Keyword *</Label>
            <Input
              id="article-keyword"
              placeholder="e.g. crypto trading bot"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              required
            />
          </div>

          {/* More options */}
          <button
            onClick={() => setShowMore(!showMore)}
            aria-expanded={showMore}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown className={cn("w-3 h-3 transition-transform", showMore && "rotate-180")} aria-hidden="true" />
            More Options
          </button>

          {showMore && (
            <div className="space-y-4 pl-2 border-l-2 border-border/40">
              {/* Target word count */}
              <div className="space-y-2">
                <Label htmlFor="article-word-count" className="text-xs">Target Word Count</Label>
                <Input
                  id="article-word-count"
                  type="number"
                  placeholder="e.g. 2000"
                  value={targetWordCount}
                  onChange={(e) => setTargetWordCount(e.target.value ? parseInt(e.target.value, 10) : "")}
                />
              </div>

              {/* Search intent */}
              <div className="space-y-2">
                <Label htmlFor="article-intent" className="text-xs">Search Intent</Label>
                <Select value={intent} onValueChange={setIntent}>
                  <SelectTrigger id="article-intent" className="h-8 text-sm">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="informational">Informational</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="transactional">Transactional</SelectItem>
                    <SelectItem value="navigational">Navigational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Scheduled publish date */}
              <div className="space-y-2">
                <Label className="text-xs">Scheduled Publish Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !publishDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {publishDate ? format(publishDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={publishDate}
                      onSelect={setPublishDate}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Auto social */}
              <div className="flex items-center justify-between">
                <Label htmlFor="article-auto-social" className="text-xs">Auto-generate social posts</Label>
                <Switch id="article-auto-social" checked={autoSocial} onCheckedChange={setAutoSocial} />
              </div>
            </div>
          )}

          {/* Submit buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              className="flex-1"
              onClick={() => handleSubmit(false)}
              disabled={!keyword.trim() || submitting}
            >
              {publishDate ? "Schedule" : "Queue"}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleSubmit(true)}
              disabled={!keyword.trim() || submitting}
            >
              {publishDate ? "Schedule" : "Queue"} & Start
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
